import { useState, useCallback, useRef } from 'react';
import { ITripGroup } from '../types/trip.types';
import { findFirstPendingTripIndex, findNextPendingTrip } from '../services/trip.service';
import { areAllTripsResolved } from '../utils/trip-navigation.utils';

interface SidebarControllerContext {
    tripGroups: ITripGroup[];
    selectedTripIndex: number;
    viewType: 'trip' | 'overall';
    isLoading: boolean;
    previousTripGroups: ITripGroup[];
    lastOpenedTripId: number | null;
    requestId: number;
}

interface SidebarDecision {
    action: 'open' | 'close' | 'none';
    tripGroup: ITripGroup | null;
    reason: string;
}

interface UseSidebarControllerProps {
    onOpenSidebar: (tripGroup: ITripGroup, loadFirstOnly?: boolean) => Promise<void>;
    onCloseSidebar: () => void;
    onNavigateToTrip: (index: number, viewType: 'trip' | 'overall') => void;
    onSetLastOpenedTripId: (tripId: number | null) => void;
}

export const useSidebarController = (props: UseSidebarControllerProps) => {
    const [hasAutoOpenedForRequest, setHasAutoOpenedForRequest] = useState<number | null>(null);
    const [lastOpenedTripId, setLastOpenedTripId] = useState<number | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
    const previousRequestIdRef = useRef<number | null>(null);
    const previousTripGroupsRef = useRef<ITripGroup[]>([]);
    const manualOpenInProgressRef = useRef<number | null>(null);

    const evaluateSidebarState = useCallback((context: SidebarControllerContext): SidebarDecision => {
        const { tripGroups, selectedTripIndex, viewType, isLoading, previousTripGroups, lastOpenedTripId: contextLastOpenedTripId, requestId } = context;

        if (isLoading || tripGroups.length === 0) {
            return { action: 'none', tripGroup: null, reason: 'loading_or_empty' };
        }

        if (manualOpenInProgressRef.current !== null) {
            return { action: 'none', tripGroup: null, reason: 'manual_open_in_progress' };
        }

        if (requestId !== previousRequestIdRef.current) {
            previousRequestIdRef.current = requestId;
            setHasAutoOpenedForRequest(null);
            setLastOpenedTripId(null);
        }

        const groupsChanged = tripGroups.length !== previousTripGroupsRef.current.length ||
            tripGroups.some((g, idx) => {
                const prev = previousTripGroupsRef.current[idx];
                return !prev || prev.id !== g.id || prev.status !== g.status;
            });

        if (groupsChanged) {
            previousTripGroupsRef.current = [...tripGroups];
        }

        if (previousTripGroups.length > 0 && selectedTripIndex < previousTripGroups.length && groupsChanged) {
            const previousTrip = previousTripGroups[selectedTripIndex];
            const wasPending = previousTrip && (previousTrip.status === 'pending' || previousTrip.status === 'in_review');
            const currentTrip = tripGroups[selectedTripIndex];
            const isNowResolved = currentTrip && (currentTrip.status === 'approved' || currentTrip.status === 'declined');
            const allTripsResolved = areAllTripsResolved(tripGroups);

            if (wasPending && isNowResolved && !allTripsResolved) {
                const nextIndex = findNextPendingTrip(tripGroups, selectedTripIndex);
                if (nextIndex !== null) {
                    const nextTrip = tripGroups[nextIndex];
                    const hasReceiptFiles = nextTrip.lineItems.some(li => li.receiptFileId);
                    if (hasReceiptFiles) {
                        props.onSetLastOpenedTripId(null);
                        setLastOpenedTripId(null);
                        props.onNavigateToTrip(nextIndex, 'trip');
                        return { action: 'open', tripGroup: nextTrip, reason: 'trip_resolved_navigate_next' };
                    }
                }
            }
        }

        if (viewType === 'overall') {
            const allResolved = areAllTripsResolved(tripGroups);
            if (!allResolved) {
                const firstPendingIndex = findFirstPendingTripIndex(tripGroups);
                if (firstPendingIndex !== null) {
                    const firstPendingTrip = tripGroups[firstPendingIndex];
                    const hasReceiptFiles = firstPendingTrip.lineItems.some(li => li.receiptFileId);
                    if (hasReceiptFiles && contextLastOpenedTripId !== firstPendingTrip.id) {
                        props.onNavigateToTrip(firstPendingIndex, 'trip');
                        props.onSetLastOpenedTripId(null);
                        return { action: 'open', tripGroup: firstPendingTrip, reason: 'view_type_overall_to_trip' };
                    }
                }
            }
            return { action: 'none', tripGroup: null, reason: allResolved ? 'view_type_overall_all_resolved' : 'view_type_overall' };
        }

        if (viewType === 'trip') {
            if (selectedTripIndex < 0 || selectedTripIndex >= tripGroups.length) {
                const firstPendingIndex = findFirstPendingTripIndex(tripGroups);
                if (firstPendingIndex !== null) {
                    const firstPendingTrip = tripGroups[firstPendingIndex];
                    const hasReceiptFiles = firstPendingTrip.lineItems.some(li => li.receiptFileId);
                    if (hasReceiptFiles) {
                        props.onNavigateToTrip(firstPendingIndex, 'trip');
                        props.onSetLastOpenedTripId(null);
                        return { action: 'open', tripGroup: firstPendingTrip, reason: 'index_out_of_bounds' };
                    }
                }
                return { action: 'none', tripGroup: null, reason: 'index_out_of_bounds' };
            }

            const currentTrip = tripGroups[selectedTripIndex];
            if (!currentTrip) {
                return { action: 'none', tripGroup: null, reason: 'no_current_trip' };
            }

            if (isSidebarOpen && lastOpenedTripId === currentTrip.id) {
                return { action: 'none', tripGroup: null, reason: 'sidebar_already_open_for_trip' };
            }

            if (currentTrip.status === 'approved' || currentTrip.status === 'declined') {
                if (isSidebarOpen && lastOpenedTripId === currentTrip.id) {
                    const firstPendingIndex = findFirstPendingTripIndex(tripGroups);
                    if (firstPendingIndex !== null && firstPendingIndex !== selectedTripIndex) {
                        const firstPendingTrip = tripGroups[firstPendingIndex];
                        const hasReceiptFiles = firstPendingTrip.lineItems.some(li => li.receiptFileId);
                        if (hasReceiptFiles) {
                            props.onNavigateToTrip(firstPendingIndex, 'trip');
                            props.onSetLastOpenedTripId(null);
                            return { action: 'open', tripGroup: firstPendingTrip, reason: 'current_trip_resolved_navigate_from_open' };
                        }
                    }
                    return { action: 'none', tripGroup: null, reason: 'sidebar_open_for_resolved_trip' };
                }
                
                const firstPendingIndex = findFirstPendingTripIndex(tripGroups);
                if (firstPendingIndex !== null && firstPendingIndex !== selectedTripIndex) {
                    const firstPendingTrip = tripGroups[firstPendingIndex];
                    const hasReceiptFiles = firstPendingTrip.lineItems.some(li => li.receiptFileId);
                    if (hasReceiptFiles) {
                        props.onNavigateToTrip(firstPendingIndex, 'trip');
                        props.onSetLastOpenedTripId(null);
                        return { action: 'open', tripGroup: firstPendingTrip, reason: 'current_trip_resolved' };
                    }
                }
                return { action: 'none', tripGroup: null, reason: 'current_trip_resolved_no_pending' };
            }

            const tripIdChanged = currentTrip.id !== contextLastOpenedTripId || currentTrip.id !== lastOpenedTripId;
            const sidebarNotOpenForTrip = !isSidebarOpen || (isSidebarOpen && lastOpenedTripId !== currentTrip.id);
            
            if (tripIdChanged || sidebarNotOpenForTrip) {
                const hasReceiptFiles = currentTrip.lineItems.some(li => li.receiptFileId);
                if (hasReceiptFiles) {
                    return { action: 'open', tripGroup: currentTrip, reason: 'trip_changed' };
                }
            }

            if (hasAutoOpenedForRequest !== requestId && currentTrip.lineItems.some(li => li.receiptFileId)) {
                const firstPendingIndex = findFirstPendingTripIndex(tripGroups);
                if (firstPendingIndex === selectedTripIndex) {
                    return { action: 'open', tripGroup: currentTrip, reason: 'initial_load' };
                }
            }
        }

        return { action: 'none', tripGroup: null, reason: 'no_action_needed' };
    }, [props, isSidebarOpen, lastOpenedTripId, hasAutoOpenedForRequest]);

    const executeDecision = useCallback(async (decision: SidebarDecision, requestId: number, contextLastOpenedTripId: number | null) => {
        if (manualOpenInProgressRef.current !== null) {
            return;
        }

        if (decision.action === 'open' && decision.tripGroup) {
            const tripGroup = decision.tripGroup;
            const tripId = tripGroup.id;
            
            const hasReceiptFiles = tripGroup.lineItems.some(li => li.receiptFileId);
            
            if (!hasReceiptFiles) {
                return;
            }

            if (lastOpenedTripId === tripId && contextLastOpenedTripId === tripId && isSidebarOpen) {
                return;
            }

            if (decision.reason === 'trip_resolved_navigate_next' || decision.reason === 'current_trip_resolved_navigate_from_open') {
                if (isSidebarOpen) {
                    props.onCloseSidebar();
                    setLastOpenedTripId(null);
                    props.onSetLastOpenedTripId(null);
                    setIsSidebarOpen(false);
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            if (decision.reason === 'initial_load') {
                setHasAutoOpenedForRequest(requestId);
            }

            setLastOpenedTripId(tripId);
            props.onSetLastOpenedTripId(tripId);
            setIsSidebarOpen(true);
            
            try {
                await props.onOpenSidebar(tripGroup, true);
            } catch (error) {
            }
        } else if (decision.action === 'close') {
            props.onCloseSidebar();
            setLastOpenedTripId(null);
            props.onSetLastOpenedTripId(null);
            setIsSidebarOpen(false);
        }
    }, [props, lastOpenedTripId, isSidebarOpen]);

    const resetForNewRequest = useCallback((requestId: number) => {
        if (requestId !== previousRequestIdRef.current) {
            previousRequestIdRef.current = requestId;
            setHasAutoOpenedForRequest(null);
            setLastOpenedTripId(null);
            previousTripGroupsRef.current = [];
        }
    }, []);

    const handleManualOpen = useCallback(async (tripGroup: ITripGroup, forceOpen: boolean = false) => {
        const tripId = tripGroup.id;

        if (lastOpenedTripId !== tripId || forceOpen || !isSidebarOpen) {
            manualOpenInProgressRef.current = tripId;
            
            if (isSidebarOpen) {
                props.onCloseSidebar();
                setIsSidebarOpen(false);
                setLastOpenedTripId(null);
                props.onSetLastOpenedTripId(null);
                await new Promise(resolve => setTimeout(resolve, 150));
            }
            
            setLastOpenedTripId(tripId);
            props.onSetLastOpenedTripId(tripId);
            setIsSidebarOpen(true);
            
            try {
                await props.onOpenSidebar(tripGroup, true);
                manualOpenInProgressRef.current = null;
            } catch (error) {
                setIsSidebarOpen(false);
                setLastOpenedTripId(null);
                props.onSetLastOpenedTripId(null);
                manualOpenInProgressRef.current = null;
            }
        }
    }, [props, lastOpenedTripId, isSidebarOpen]);

    const handleManualClose = useCallback(() => {
        props.onCloseSidebar();
        setLastOpenedTripId(null);
        props.onSetLastOpenedTripId(null);
        setIsSidebarOpen(false);
    }, [props]);

    const isManualOpenInProgress = useCallback(() => {
        return manualOpenInProgressRef.current !== null;
    }, []);

    return {
        evaluateSidebarState,
        executeDecision,
        resetForNewRequest,
        handleManualOpen,
        handleManualClose,
        isSidebarOpen,
        isManualOpenInProgress
    };
};

